import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { listChannels } from "@kritaka/core";
import { useEffect, useState } from "react";
import { useDataContext } from "../hub/data-context-provider.js";
const FORMAT_OPTIONS = ["raw", "linear", "github"];
const FORMAT_COLORS = {
    linear: "#5E6AD2",
    github: "#238636",
    raw: "var(--ink-4)",
};
/**
 * Webhook routes admin (Integrations tab) — KTK-275 redesign.
 *
 * Lists configured routes, allows adding new ones with a known format
 * (raw / linear / github) targeting a specific channel, and deleting
 * existing ones. Public webhook URL is displayed per-route for copy.
 */
export function WebhookDialog({ workspaceId, onClose, inline = false }) {
    const ctx = useDataContext();
    const [routes, setRoutes] = useState(null);
    const [channels, setChannels] = useState([]);
    const [showAdd, setShowAdd] = useState(false);
    const [newName, setNewName] = useState("");
    const [newChannel, setNewChannel] = useState("");
    const [newFormat, setNewFormat] = useState("raw");
    const [newSecret, setNewSecret] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [copiedRoute, setCopiedRoute] = useState(null);
    const refresh = async () => {
        try {
            const r = await ctx.hub.listWebhookRoutes(workspaceId);
            setRoutes(r);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
    };
    useEffect(() => {
        let cancelled = false;
        Promise.all([
            ctx.hub.listWebhookRoutes(workspaceId),
            listChannels(ctx, workspaceId),
        ])
            .then(([r, c]) => {
            if (cancelled)
                return;
            setRoutes(r);
            setChannels(c);
        })
            .catch((e) => {
            if (!cancelled)
                setError(e instanceof Error ? e.message : String(e));
        });
        return () => {
            cancelled = true;
        };
    }, [ctx, workspaceId]);
    async function handleAdd() {
        const name = newName
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9-]/g, "-")
            .replace(/-+/g, "-")
            .replace(/^-|-$/g, "");
        if (!name || !newChannel)
            return;
        setSaving(true);
        setError(null);
        try {
            await ctx.hub.createWebhookRoute(workspaceId, name, newChannel, newFormat, newSecret.trim() || undefined);
            setNewName("");
            setNewChannel("");
            setNewFormat("raw");
            setNewSecret("");
            setShowAdd(false);
            await refresh();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
        finally {
            setSaving(false);
        }
    }
    async function handleDelete(routeName) {
        if (!window.confirm(`Delete webhook route "${routeName}"?`))
            return;
        try {
            await ctx.hub.deleteWebhookRoute(workspaceId, routeName);
            await refresh();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
    }
    function handleCopy(routeName, url) {
        void navigator.clipboard.writeText(url).then(() => {
            setCopiedRoute(routeName);
            window.setTimeout(() => {
                setCopiedRoute((c) => (c === routeName ? null : c));
            }, 1500);
        }, () => undefined);
    }
    const body = (_jsxs(_Fragment, { children: [_jsxs("header", { className: "ws-sect__head", children: [_jsx("h2", { className: "ws-sect__h", children: "Webhook routes" }), _jsx("p", { className: "ws-sect__sub", children: "Inbound. Each route deposits a message in the target channel; raw / linear / github formats decode the payload. Optional HMAC signing." })] }), _jsxs("div", { className: "ws-card ws-card--flush", children: [_jsxs("div", { className: "ws-toolbar", children: [_jsx("span", { className: "ws-toolbar__l", children: routes ? (_jsxs("span", { className: "ws-mono", children: [routes.length, " route", routes.length !== 1 ? "s" : ""] })) : (_jsx("span", { className: "ws-mono", children: "Loading\u2026" })) }), _jsx("button", { type: "button", className: `ws-btn${showAdd ? "" : " ws-btn--primary"}`, onClick: () => setShowAdd(!showAdd), children: showAdd ? "Cancel" : "+ Add route" })] }), showAdd && (_jsxs("form", { onSubmit: (e) => {
                            e.preventDefault();
                            void handleAdd();
                        }, style: {
                            display: "grid",
                            gap: 10,
                            padding: "14px",
                            background: "var(--paper-3)",
                            borderBottom: "1px solid var(--hairline-2)",
                        }, children: [_jsxs("div", { className: "ws-fieldrow", children: [_jsxs("label", { className: "ws-field", children: [_jsx("span", { className: "ws-field__l", children: "Route name" }), _jsx("input", { type: "text", className: "ws-field__i mono", value: newName, onChange: (e) => setNewName(e.target.value), placeholder: "linear-ingest", required: true }), _jsx("span", { className: "ws-field__h", children: "Lowercase, a\u2013z 0\u20139 hyphens. Forms the public URL path." })] }), _jsxs("label", { className: "ws-field", children: [_jsx("span", { className: "ws-field__l", children: "Target channel" }), _jsxs("select", { className: "ws-field__i", value: newChannel, onChange: (e) => setNewChannel(e.target.value), required: true, children: [_jsx("option", { value: "", children: "Choose\u2026" }), channels.map((c) => (_jsxs("option", { value: c.id, children: ["#", c.name] }, c.id)))] })] })] }), _jsxs("div", { className: "ws-fieldrow", children: [_jsxs("label", { className: "ws-field", children: [_jsx("span", { className: "ws-field__l", children: "Format" }), _jsx("select", { className: "ws-field__i", value: newFormat, onChange: (e) => setNewFormat(e.target.value), children: FORMAT_OPTIONS.map((f) => (_jsx("option", { value: f, children: f }, f))) })] }), _jsxs("label", { className: "ws-field", children: [_jsx("span", { className: "ws-field__l", children: "Signing secret (optional)" }), _jsx("input", { 
                                                // type="text" + CSS masking — see the matching rationale
                                                // in the OpenRouter / Brave key fields above. Browsers
                                                // ignore `type="password"` autocomplete opt-outs and
                                                // pop a "save password?" prompt; this avoids that.
                                                type: "text", className: "ws-field__i mono", value: newSecret, onChange: (e) => setNewSecret(e.target.value), placeholder: "HMAC secret", name: "webhook-signing-secret-anon", autoComplete: "off", "data-1p-ignore": "true", "data-lpignore": "true", "data-form-type": "other", spellCheck: false, style: { WebkitTextSecurity: "disc" } })] })] }), _jsxs("div", { className: "ws-actions", children: [_jsx("span", { className: "ws-actions__hint" }), _jsx("button", { type: "submit", className: "ws-btn ws-btn--primary", disabled: saving, children: saving ? "Creating…" : "Create route" })] })] })), error && (_jsx("div", { style: {
                            margin: "8px 14px",
                            color: "var(--error)",
                            fontSize: 12,
                            padding: "8px 10px",
                            border: "1px solid rgba(250,82,82,0.3)",
                            borderRadius: 6,
                        }, children: error })), _jsx("div", { className: "ws-routes", children: !routes ? null : routes.length === 0 ? (_jsx("div", { style: {
                                padding: "20px 14px",
                                textAlign: "center",
                                color: "var(--ink-3)",
                                fontFamily: "var(--serif)",
                                fontStyle: "italic",
                                fontSize: 12.5,
                            }, children: "No webhook routes configured." })) : (routes.map((r) => {
                            const targetChannel = channels.find((c) => c.id === r.target_channel);
                            const url = `https://api.kritaka.io/webhooks/${workspaceId}/${r.route_name}`;
                            const copied = copiedRoute === r.route_name;
                            return (_jsxs("div", { className: "ws-route", children: [_jsxs("div", { className: "ws-route__main", children: [_jsxs("div", { className: "ws-route__head", children: [_jsx("span", { className: "ws-route__name", children: r.route_name }), _jsx("span", { className: "ws-route__fmt", style: {
                                                            background: FORMAT_COLORS[r.format] ?? "var(--ink-4)",
                                                        }, children: r.format })] }), _jsxs("div", { className: "ws-route__sub", children: ["\u2192 #", targetChannel?.name ?? r.target_channel, r.signing_secret && " · signed"] }), _jsx("div", { className: "ws-route__url", title: url, children: url })] }), _jsx("button", { type: "button", className: "ws-icon-btn", onClick: () => handleCopy(r.route_name, url), title: copied ? "Copied" : "Copy URL", "aria-label": copied ? "Copied" : "Copy URL", children: copied ? "✓" : "⧉" }), _jsx("button", { type: "button", className: "ws-icon-btn ws-icon-btn--danger", onClick: () => void handleDelete(r.route_name), title: "Delete", "aria-label": "Delete route", children: "\u00D7" })] }, r.route_name));
                        })) })] }), !inline && (_jsxs("div", { className: "ws-actions", children: [_jsx("span", { className: "ws-actions__hint" }), _jsx("button", { type: "button", className: "ws-btn", onClick: onClose, children: "Close" })] }))] }));
    if (inline) {
        return body;
    }
    return (_jsx("div", { style: modalBackdrop, children: _jsx("div", { onClick: (e) => e.stopPropagation(), style: {
                ...modalPanel,
                width: 720,
                maxHeight: "85vh",
                overflow: "auto",
            }, children: body }) }));
}
const modalBackdrop = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "grid",
    placeItems: "center",
    zIndex: 100,
};
const modalPanel = {
    background: "var(--paper)",
    border: "1px solid var(--hairline)",
    borderRadius: 10,
    padding: 20,
    display: "flex",
    flexDirection: "column",
    gap: 12,
};
//# sourceMappingURL=WebhookDialog.js.map